import { Button, Stack, type SxProps, type Theme, Typography } from '@mui/material';
import { V2ThemeProvider } from '@onevision-resources/design-theme';
import React from 'react';
import { DynamicIcon } from 'src/components';
import {
  type TriageAction,
  type TriageRun,
  TriageRunProgress,
} from 'src/graphql/entities/conversation-triage-run';
import { CONVERSATION_ROW_GUTTER } from 'src/pages/Conversations/constants';
import { useDisplayTime } from 'src/utils/hooks';
import {
  AppliedChip,
  DismissedChip,
  FailedChip,
  SuggestedChip,
} from './components/triage-action-chips';
import {
  LandingSlot,
  ShimmerText,
  ShimmerTitle,
  StatusOrb,
  TriageAvatar,
  TriageBox,
  TriagePropertyStyles,
} from './triage-run-row-view.styles';

const outerRowSx: SxProps<Theme> = {
  mt: 1,
  mb: 1,
  width: '100%',
  // gutter minus the avatar circle (1.75rem) and the row gap (1.25 → 0.625rem)
  pl: `calc(${CONVERSATION_ROW_GUTTER} - 1.75rem - 0.625rem)`,
  pr: 2,
  boxSizing: 'border-box',
};

const labelSx: SxProps<Theme> = {
  flex: '0 0 5.25rem',
  // Match the chip row height and center, so the label aligns with the first
  // row of pills (which have the same min height).
  minHeight: '1.625rem',
  display: 'flex',
  alignItems: 'center',
  fontSize: '0.75rem',
  color: (theme) => theme.palette.custom.OV.sys.onSurfaceVariant,
};

const timeSx: SxProps<Theme> = {
  ml: 'auto',
  fontSize: '0.6875rem',
  fontVariantNumeric: 'tabular-nums',
  color: (theme) => theme.palette.custom.OV.sys.onSurfaceVariant,
};

export interface TriageRunRowViewProps {
  run: TriageRun;
  isResolving: (actionId: string) => boolean;
  onAccept: (actionId: string) => void;
  onDismiss: (actionId: string) => void;
  onRetry: (actionId: string) => void;
  onAcceptAll: () => void;
}

const GroupRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.JSX.Element => (
  <Stack direction="row" alignItems="flex-start" gap={1.75} sx={{ py: 0.375 }}>
    <Typography sx={labelSx}>{label}</Typography>
    <Stack
      direction="row"
      flexWrap="wrap"
      useFlexGap
      alignItems="center"
      gap={0.75}
      sx={{ flex: '1 1 0', minWidth: 0 }}
    >
      {children}
    </Stack>
  </Stack>
);

export const TriageRunRowView = ({
  run,
  isResolving,
  onAccept,
  onDismiss,
  onRetry,
  onAcceptAll,
}: TriageRunRowViewProps): React.JSX.Element => {
  const { displayTime } = useDisplayTime();
  const isRunning = run.progress === TriageRunProgress.Running;
  const { applied, suggested, failed, dismissed } = run.groups;
  const showAcceptAll = !isRunning && run.pendingSuggestedCount >= 2;

  const renderChips = (
    actions: TriageAction[],
    render: (action: TriageAction) => React.ReactNode,
  ) => actions.map((action) => <LandingSlot key={action.id}>{render(action)}</LandingSlot>);

  return (
    // Opt the panel into v2 so triage reason tooltips render as v2 surface cards.
    <V2ThemeProvider>
      <Stack direction="row" gap={1.25} sx={outerRowSx}>
        <TriagePropertyStyles />
        <TriageAvatar running={isRunning}>
          <DynamicIcon name="sparkles" library="lucide" className="triage-avatar-icon" />
        </TriageAvatar>

        <TriageBox running={isRunning} data-testid={`triage-run-${run.id}`}>
          <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1.25 }}>
            <ShimmerTitle running={isRunning}>Triage Intelligence</ShimmerTitle>
            <Typography sx={timeSx}>{displayTime(run.timestamp)}</Typography>
          </Stack>

          {applied.length > 0 ? (
            <GroupRow label="Applied">
              {renderChips(applied, (action) => (
                <AppliedChip action={action} />
              ))}
            </GroupRow>
          ) : null}

          {suggested.length > 0 ? (
            <GroupRow label="Suggested">
              {renderChips(suggested, (action) => (
                <SuggestedChip
                  action={action}
                  isResolving={isResolving(action.id)}
                  onAccept={onAccept}
                  onDismiss={onDismiss}
                />
              ))}
            </GroupRow>
          ) : null}

          {failed.length > 0 ? (
            <GroupRow label="Failed">
              {renderChips(failed, (action) => (
                <FailedChip
                  action={action}
                  isResolving={isResolving(action.id)}
                  onRetry={onRetry}
                />
              ))}
            </GroupRow>
          ) : null}

          {dismissed.length > 0 ? (
            <GroupRow label="Dismissed">
              {renderChips(dismissed, (action) => (
                <DismissedChip action={action} />
              ))}
            </GroupRow>
          ) : null}

          {isRunning ? (
            <Stack
              direction="row"
              alignItems="center"
              gap={1}
              sx={{ minHeight: '1.375rem', pt: 0.25 }}
            >
              <StatusOrb />
              <ShimmerText>Triaging conversation…</ShimmerText>
            </Stack>
          ) : null}

          {showAcceptAll ? (
            <Stack direction="row" justifyContent="flex-end" pt={1}>
              <Button
                size="small"
                variant="outlined"
                startIcon={
                  <DynamicIcon
                    name="list-checks"
                    library="lucide"
                    sx={{ fontSize: '0.8125rem' }}
                  />
                }
                onClick={onAcceptAll}
                sx={{ textTransform: 'none' }}
              >
                Accept all
              </Button>
            </Stack>
          ) : null}
        </TriageBox>
      </Stack>
    </V2ThemeProvider>
  );
};
